import styles from './AleloLayout.module.css'
import type { VerificationChannel } from '@/utils/signupVerificationApi'

const CHANNEL_LABELS: Record<VerificationChannel, string> = {
  email: 'Confirmar e-mail',
  sms: 'Confirmar celular',
}

/** Canais da jornada completa — o que as telas de MFA pós-login mostram. */
const DEFAULT_CHANNELS: VerificationChannel[] = ['email', 'sms']

/** Etapa final do Identifier First: a senha vem depois dos contatos. */
const PASSWORD_LABEL = 'Criar senha'

/**
 * Canais que o stepper desenha. Quando o BFF assume algum canal, são só
 * esses; ausente ou vazio, a confirmação fica para as telas de MFA pós-login
 * (passos 2 e 3), então o stepper mostra a jornada completa.
 */
function stepperChannels(channels?: VerificationChannel[] | null) {
  return channels && channels.length > 0 ? channels : DEFAULT_CHANNELS
}

/**
 * Rótulos na ordem em que as telas aparecem. `withPassword` acrescenta a
 * última etapa do Identifier First, em que `signup-password` cria a conta —
 * as telas que terminam antes dela (cadastro numa página só, MFA pós-login)
 * deixam a flag de fora.
 */
function stepperLabels(
  channels?: VerificationChannel[] | null,
  withPassword = false,
) {
  return [
    'Dados',
    ...stepperChannels(channels).map((channel) => CHANNEL_LABELS[channel]),
    ...(withPassword ? [PASSWORD_LABEL] : []),
  ]
}

/** Total de etapas para o texto "Passo N de M". */
export function stepperTotal(
  channels?: VerificationChannel[] | null,
  withPassword = false,
) {
  return stepperLabels(channels, withPassword).length
}

type StepperProps = {
  currentStep: number
  /** Canais que o BFF exige; ver `stepperChannels` para ausente/vazio. */
  channels?: VerificationChannel[] | null
  /** Desenha "Criar senha" como etapa final; ver `stepperLabels`. */
  withPassword?: boolean
}

/**
 * Indicador visual das etapas do cadastro: Dados, mais uma etapa por canal
 * exigido (Confirmar e-mail / Confirmar celular), mais a senha quando a
 * jornada termina em `signup-password`.
 */
export function AleloStepper({
  currentStep,
  channels,
  withPassword,
}: StepperProps) {
  const labels = stepperLabels(channels, withPassword)

  return (
    <ol
      className={styles.stepper}
      aria-label={`Passo ${currentStep} de ${labels.length}`}
    >
      {labels.map((label, index) => {
        const step = index + 1
        const status =
          step < currentStep ? 'done' : step === currentStep ? 'active' : 'upcoming'
        return (
          <li key={label} className={styles.stepperItem} data-status={status}>
            <span className={styles.stepperDot} aria-hidden="true">
              {step}
            </span>
            <span className={styles.stepperLabel}>{label}</span>
          </li>
        )
      })}
    </ol>
  )
}
